import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { ComicButton, ComicHeading, ComicPanel } from '../components/ComicPanel';
import { Layout } from '../components/layout/Layout';
import { ChevronRight, Zap, BookOpen, Users } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Landing = () => {
  const { user } = useAuth();
  const { amazonBuyLink, isInteractiveLive, interactiveCtaLabel } = useSite();

  const interactiveLink = isInteractiveLive ? '/dashboard' : '/donate';

  return (
    <Layout>
      {/* HERO */}
      <section className="relative bg-slate-950 pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(0,229,229,0.08),transparent_40%)]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center">
            <div className="w-full max-w-[460px] lg:max-w-[520px] rounded-[20px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.65)] border border-slate-800 bg-black">
              <img
                src="/images/cover-test3.png"
                alt="Pork Cops Episode 1 First Patrol cover"
                className="block w-full h-auto"
              />
            </div>

            <p className="mt-8 text-slate-200 text-lg sm:text-xl max-w-2xl">
              In a world powered by SPORC... justice is enforced by Pork Cops.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to={interactiveLink}>
                <button
                  className="inline-flex items-center justify-center font-black uppercase tracking-wider px-6 py-4 border-2 border-[#00FFFF] text-[#041014] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] min-w-[280px]"
                  style={{
                    background: 'linear-gradient(90deg, #00FFFF 0%, #00E5E5 50%, #00BFCF 100%)',
                  }}
                  data-testid="cta-interactive"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  {interactiveCtaLabel}
                </button>
              </Link>

              <a href={amazonBuyLink} target="_blank" rel="noopener noreferrer">
                <button
                  className="inline-flex items-center justify-center font-black uppercase tracking-wider text-white px-6 py-4 border-2 border-[#2FB4E9] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] min-w-[280px]"
                  style={{
                    background: '#2FB4E9',
                  }}
                  data-testid="cta-readers-cut"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Buy Reader&apos;s Cut
                </button>
              </a>
            </div>

            <div className="mt-10 animate-bounce">
              <ChevronRight className="w-8 h-8 text-[#00E5E5] rotate-90" />
            </div>
          </div>
        </div>
      </section>

      {/* TWO WAYS TO READ */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ComicHeading
            as="h2"
            className="text-3xl sm:text-4xl text-center mb-12"
            data-testid="section-ways-to-read"
          >
            Two Ways to Experience Pork Cops
          </ComicHeading>

          <div className="grid md:grid-cols-2 gap-8">
            <ComicPanel className="p-8 text-center">
              <div
                className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border-2 border-[#00FFFF]"
                style={{
                  background: 'linear-gradient(90deg, #00FFFF 0%, #00E5E5 50%, #00BFCF 100%)',
                }}
              >
                <Zap className="w-8 h-8 text-[#041014]" />
              </div>

              <h3 className="text-[#00E5E5] uppercase font-bold tracking-widest text-xl mb-4">
                Interactive Cut
              </h3>

              <p className="text-slate-300 mb-6">
                Make choices that shape the story. Your decisions matter. Free with membership.
              </p>

              <Link to={interactiveLink}>
                <button
                  className="w-full inline-flex items-center justify-center font-black uppercase tracking-wider px-6 py-4 border-2 border-[#00FFFF] text-[#041014] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                  style={{
                    background: 'linear-gradient(90deg, #00FFFF 0%, #00E5E5 50%, #00BFCF 100%)',
                  }}
                  data-testid="card-interactive-cta"
                >
                  {isInteractiveLive ? 'Start Reading' : 'Coming Soon'}
                </button>
              </Link>
            </ComicPanel>

            <ComicPanel className="p-8 text-center">
              <div
                className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border-2 border-[#2FB4E9]"
                style={{
                  background: '#2FB4E9',
                }}
              >
                <BookOpen className="w-8 h-8 text-white" />
              </div>

              <h3 className="text-[#FF6FAF] uppercase font-bold tracking-widest text-xl mb-4">
                Reader&apos;s Cut
              </h3>

              <p className="text-slate-300 mb-6">
                The complete story as intended. Available now on Amazon.
              </p>

              <a href={amazonBuyLink} target="_blank" rel="noopener noreferrer">
                <button
                  className="w-full inline-flex items-center justify-center font-black uppercase tracking-wider text-white px-6 py-4 border-2 border-[#2FB4E9] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                  style={{
                    background: '#2FB4E9',
                  }}
                  data-testid="card-readers-cta"
                >
                  Buy on Amazon
                </button>
              </a>
            </ComicPanel>
          </div>
        </div>
      </section>

      {/* JOIN */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="w-20 h-20 mx-auto mb-6 flex items-center justify-center border-2 border-[#00FFFF]"
            style={{
              background: 'linear-gradient(90deg, #00FFFF 0%, #00E5E5 50%, #00BFCF 100%)',
            }}
          >
            <Users className="w-10 h-10 text-[#041014]" />
          </div>

          <ComicHeading as="h2" className="text-3xl sm:text-4xl mb-6" data-testid="section-join">
            Join the Piggee Pen Club
          </ComicHeading>

          <p className="text-slate-300 text-lg mb-8">
            Create a free account to access exclusive skits, bonus content, and updates.
            No spam, just Sporc and Pork Cops!
          </p>

          {user ? (
            <Link to="/dashboard">
              <ComicButton variant="secondary" data-testid="cta-go-dashboard">
                Go to Dashboard
              </ComicButton>
            </Link>
          ) : (
            <Link to="/signup">
              <button
                className="inline-flex items-center justify-center font-black uppercase tracking-wider text-[#041014] px-6 py-4 border-2 border-[#00FFFF] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                style={{
                  background: 'linear-gradient(90deg, #00FFFF 0%, #00E5E5 50%, #00BFCF 100%)',
                }}
                data-testid="cta-join-free"
              >
                Join for Free Now
              </button>
            </Link>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ComicHeading as="h2" className="text-3xl sm:text-4xl text-center mb-12" data-testid="section-faq">
            FAQ
          </ComicHeading>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border-2 border-slate-700 bg-slate-800 px-6">
              <AccordionTrigger className="text-left font-bold text-white hover:text-[#00E5E5]">
                What is Pork Cops?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                On a world powered by Sporc energy, pigs live easy—until the cows take over. Now rookie Pork Cop Hamee Hocks is thrown into a fight he’s not ready for… against an enemy that controls everything.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border-2 border-slate-700 bg-slate-800 px-6">
              <AccordionTrigger className="text-left font-bold text-white hover:text-[#00E5E5]">
                What&apos;s the difference between Interactive and Reader&apos;s Cut?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                Play it… or read it. The Interactive Cut lets you control how the story unfolds. The Reader&apos;s Cut delivers the full, definitive experience exactly as it was meant to be told.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border-2 border-slate-700 bg-slate-800 px-6">
              <AccordionTrigger className="text-left font-bold text-white hover:text-[#00E5E5]">
                Is membership really free?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                Yes! Create an account to access skits, bonus content, and updates at no cost. We may offer premium features in the future, but the core experience will always be free.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border-2 border-slate-700 bg-slate-800 px-6">
              <AccordionTrigger className="text-left font-bold text-white hover:text-[#00E5E5]">
                How can I support Pork Cops?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                The best ways to support Pork Cops: grab the Reader&apos;s Cut on Amazon, help fund new episodes with a donation, and spread the word to fellow comic fans.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </Layout>
  );
};

export default Landing;
